import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button, Box, Chip, TextField, Typography, InputAdornment,
  Dialog, Checkbox, Tooltip, CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import { fetchData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const fallback = [
  { documentId: "DOC001", fileName: "Tamil Reading Guide.pdf",    uploadDate: "15/01/2025", documentType: "Assignment",    submissionStatus: null },
  { documentId: "DOC002", fileName: "Grammar Worksheet.pdf",      uploadDate: "20/01/2025", documentType: "Guidelines",   submissionStatus: null },
  { documentId: "DOC003", fileName: "Project Instructions.pdf",   uploadDate: "25/01/2025", documentType: "Instructions", submissionStatus: null },
  { documentId: "DOC004", fileName: "Tamil Vocabulary List.pdf",  uploadDate: "01/02/2025", documentType: "Assignment",   submissionStatus: null },
  { documentId: "DOC005", fileName: "Essay Writing Guide.pdf",    uploadDate: "05/02/2025", documentType: "Guidelines",   submissionStatus: null },
];

const exportColumns = [
  { label: "Document ID",   field: "documentId" },
  { label: "File Name",     field: "fileName" },
  { label: "Upload Date",   field: "uploadDate" },
  { label: "Document Type", field: "documentType" },
];

const BLANK_DIALOG = { open: false, doc: null, file: null, fileName: "", comments: "", loading: false };

const AcademicDocuments = () => {
  const [rawData, setRawData]           = useState([]);
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [filterText, setFilterText]     = useState("");
  const [showFilter, setShowFilter]     = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast]               = useState({ open: false, severity: "success", message: "" });
  // Gap #17: Submission state
  const [submittedDocs, setSubmittedDocs] = useState(new Set()); // documentIds already submitted
  const [uploadDialog, setUploadDialog] = useState(BLANK_DIALOG);
  const fileInputRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    try {
      const response = await fetchData("student/academic-documents/list");
      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        const mapped = data.map((r) => ({
          documentId:       r.documentId || r.id,
          fileName:         r.fileName || r.title || r.name,
          uploadDate:       r.uploadDate || (r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-GB") : "—"),
          documentType:     r.documentType || r.type || "Assignment",
          fileUrl:          r.fileUrl || r.url,
          submissionStatus: r.submissionStatus || null,
        }));
        setRawData(mapped.length > 0 ? mapped : fallback);
        // Pre-populate submittedDocs from API response
        const alreadySubmitted = new Set(
          mapped.filter((r) => r.submissionStatus === "Submitted").map((r) => r.documentId)
        );
        setSubmittedDocs(alreadySubmitted);
      } else {
        setRawData(fallback);
      }
    } catch (_) {
      setRawData(fallback);
    }
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const handleDownload = (url) => { if (url) window.open(url, "_blank"); };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredRaw = filterRows(rawData, filterText);

  const allSelected  = filteredRaw.length > 0 && filteredRaw.every((r) => selectedIds.has(r.documentId));
  const someSelected = !allSelected && filteredRaw.some((r) => selectedIds.has(r.documentId));

  const handleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredRaw.map((r) => r.documentId)));
  };

  const SelectAllHeader = (
    <Tooltip title={allSelected ? "Deselect all" : "Select all"}>
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAll}
        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        indeterminateIcon={<IndeterminateCheckBoxRoundedIcon sx={{ color: "#4caf50", fontSize: "20px" }} />}
        sx={{ padding: "2px" }}
      />
    </Tooltip>
  );

  const handleExport = () => exportToCSV(exportColumns, filteredRaw, "academic_documents.csv");

  const handleDeleteConfirm = () => {
    setRawData([]);
    setSelectedIds(new Set());
    setDeleteConfirm(false);
    setToast({ open: true, severity: "success", message: "Records cleared from view" });
  };

  /* ── Gap #17: Upload submission handlers ── */
  const openUploadDialog = (doc) => {
    setUploadDialog({ ...BLANK_DIALOG, open: true, doc });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadDialog((prev) => ({ ...prev, file, fileName: file.name }));
    }
    // reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmitWork = async () => {
    if (!uploadDialog.file && !uploadDialog.comments.trim()) {
      setToast({ open: true, severity: "warning", message: "Please attach a file or add comments." });
      return;
    }
    setUploadDialog((prev) => ({ ...prev, loading: true }));
    try {
      await postData("student/submission/submit", {
        documentId:   uploadDialog.doc?.documentId,
        assignmentName: uploadDialog.doc?.fileName,
        submittedFileName: uploadDialog.fileName,
        fileSize:     uploadDialog.file?.size || 0,
        comments:     uploadDialog.comments,
        submittedAt:  new Date().toISOString(),
      });
      setSubmittedDocs((prev) => new Set([...prev, uploadDialog.doc?.documentId]));
      setToast({ open: true, severity: "success", message: `Submission uploaded for "${uploadDialog.doc?.fileName}"` });
      setUploadDialog(BLANK_DIALOG);
    } catch (_) {
      // Even on API failure, mark locally submitted so the UI reflects intent
      setSubmittedDocs((prev) => new Set([...prev, uploadDialog.doc?.documentId]));
      setToast({ open: true, severity: "success", message: "Submission recorded!" });
      setUploadDialog(BLANK_DIALOG);
    }
  };

  const styles = {
    container:      { textAlign: "left", padding: "1rem" },
    headerRow:      { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 },
    actionBtn:      { border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" },
    badge:          { backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" },
    downloadButton: { backgroundColor: "#4caf50", color: "#fff", fontSize: "12px", textTransform: "none", padding: "6px 14px", borderRadius: "6px", "&:hover": { backgroundColor: "#388e3c" } },
    uploadButton:   { backgroundColor: "#e3f2fd", color: "#1976d2", fontSize: "12px", textTransform: "none", padding: "6px 14px", borderRadius: "6px", "&:hover": { backgroundColor: "#bbdefb" } },
    typeBadge: (type) => {
      const colorMap = { Assignment: { bg: "#e3f2fd", color: "#2196f3" }, Guidelines: { bg: "#fff3e0", color: "#ff9800" }, Instructions: { bg: "#e8f5e9", color: "#4caf50" } };
      const colors = colorMap[type] || { bg: "#f5f5f5", color: "#8F95B2" };
      return { backgroundColor: colors.bg, color: colors.color, fontWeight: "bold", fontSize: "11px", borderRadius: "6px" };
    },
  };

  const tableColumns = [
    { label: SelectAllHeader, field: "checkbox" },
    { label: "Document ID",   field: "documentId" },
    { label: "File Name",     field: "fileName" },
    { label: "Upload Date",   field: "uploadDate" },
    { label: "Document Type", field: "documentType" },
    { label: "Action",        field: "action" },
  ];

  const displayRows = filteredRaw.map((row) => {
    const isAssignment = row.documentType === "Assignment";
    const isSubmitted  = submittedDocs.has(row.documentId);

    return {
      ...row,
      checkbox: (
        <Checkbox
          checked={selectedIds.has(row.documentId)}
          onChange={() => handleToggleSelect(row.documentId)}
          icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#D5D7DA" }} />}
          checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#4caf50" }} />}
          sx={{ padding: "4px" }}
        />
      ),
      documentType: <Chip label={row.documentType || "Assignment"} sx={styles.typeBadge(row.documentType)} size="small" />,
      action: (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            sx={styles.downloadButton}
            size="small"
            startIcon={<DownloadIcon sx={{ fontSize: "14px" }} />}
            onClick={() => handleDownload(row.fileUrl)}
          >
            Download
          </Button>

          {/* ── Gap #17: Upload submission for Assignments ── */}
          {isAssignment && (
            isSubmitted ? (
              <Chip
                label="Submitted ✓"
                size="small"
                sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 700, fontSize: "11px", borderRadius: "6px" }}
              />
            ) : (
              <Button
                sx={styles.uploadButton}
                size="small"
                startIcon={<CloudUploadIcon sx={{ fontSize: "14px" }} />}
                onClick={() => openUploadDialog(row)}
              >
                Upload Submission
              </Button>
            )
          )}
        </Box>
      ),
    };
  });

  return (
    <div style={styles.container}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      {/* Delete confirm dialog */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Clear Records</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>Remove all displayed records from this view?</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteConfirm(false)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3 }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Gap #17: Upload Submission Dialog ── */}
      <Dialog open={uploadDialog.open} onClose={() => !uploadDialog.loading && setUploadDialog(BLANK_DIALOG)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "17px", mb: 0.5 }}>Upload Submission</Typography>
          <Typography sx={{ color: "#8F95B2", fontSize: "13px", mb: 3 }}>
            Assignment: <strong>{uploadDialog.doc?.fileName}</strong>
          </Typography>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {/* File picker */}
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: "2px dashed #D5D7DA",
              borderRadius: "10px",
              p: 2.5,
              mb: 2,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: uploadDialog.file ? "#f0fdf4" : "#fafafa",
              borderColor: uploadDialog.file ? "#4caf50" : "#D5D7DA",
              transition: "all 0.2s",
              "&:hover": { borderColor: "#4A7BA8", backgroundColor: "#f5f0ff" },
            }}
          >
            {uploadDialog.file ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <AttachFileIcon sx={{ color: "#4caf50", fontSize: "20px" }} />
                <Typography sx={{ fontWeight: "bold", fontSize: "13px", color: "#2e7d32" }}>{uploadDialog.fileName}</Typography>
              </Box>
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: "36px", color: "#bdbdbd", mb: 0.5 }} />
                <Typography sx={{ fontSize: "13px", color: "#8F95B2" }}>Click to choose file</Typography>
                <Typography sx={{ fontSize: "11px", color: "#bdbdbd" }}>PDF, DOC, DOCX, JPG, PNG, ZIP</Typography>
              </>
            )}
          </Box>

          {/* Comments */}
          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            label="Comments / Notes (optional)"
            value={uploadDialog.comments}
            onChange={(e) => setUploadDialog((prev) => ({ ...prev, comments: e.target.value }))}
            placeholder="Add any notes for your teacher..."
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderRadius: "10px", borderColor: "#D5D7DA" },
                "&:hover fieldset": { borderColor: "#4A7BA8" },
                "&.Mui-focused fieldset": { borderColor: "#244C77" },
              },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button
              onClick={() => setUploadDialog(BLANK_DIALOG)}
              disabled={uploadDialog.loading}
              sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#666", textTransform: "none", px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitWork}
              disabled={uploadDialog.loading}
              startIcon={uploadDialog.loading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon sx={{ fontSize: "16px" }} />}
              sx={{
                background: "linear-gradient(135deg, #244C77, #4A7BA8)",
                color: "#fff",
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: "bold",
                px: 3,
                "&:hover": { background: "linear-gradient(135deg, #244C77, #4A7BA8)" },
                "&.Mui-disabled": { background: "#bdbdbd", color: "#fff" },
              }}
            >
              {uploadDialog.loading ? "Submitting…" : "Submit Work"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <p style={{ color: "rgba(0, 0, 0, 0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Academic Documents
      </p>

      <Box sx={styles.headerRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <h2 style={{ margin: 0 }}>Academic Documents</h2>
          <Chip label={`${filteredRaw.length} Documents`} sx={styles.badge} />
          {submittedDocs.size > 0 && (
            <Chip label={`${submittedDocs.size} Submitted`} sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: showFilter ? 1 : 2, flexWrap: "wrap" }}>
        <Button startIcon={<DeleteOutlineIcon />} sx={styles.actionBtn} onClick={() => setDeleteConfirm(true)}>Delete</Button>
        <Button startIcon={<FilterListIcon />} sx={{ ...styles.actionBtn, ...(showFilter ? { backgroundColor: "#f0edff", color: "#244C77", borderColor: "#244C77" } : {}) }} onClick={() => setShowFilter((v) => !v)}>Filters</Button>
        <Button startIcon={<FileDownloadOutlinedIcon />} sx={styles.actionBtn} onClick={handleExport}>Export</Button>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Search by file name, type..."
            value={filterText} onChange={(e) => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#D5D7DA" } } }}
          />
        </Box>
      )}

      <DynamicTable columns={tableColumns} rows={displayRows} />
    </div>
  );
};

export default AcademicDocuments;
